// components/Dashboard/DashboardTopMenu.jsx
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import RobotsPopUpMenu from "components/Robots/SharedRobots/PopUpMenu";
import StrategiesPopUpMenu from "components/Strategies/SharedStrategies/PopUpMenu";

import customAxios from "lib/axios";
import useRobot from "hooks/useRobot";
import useStrategy from "hooks/useStrategy";
import { show } from "store/reducers/notifications.reducer";

const apiBase = process.env.REACT_APP_TRADEASY_API || "";

function DashboardTopMenu({
  anchor,
  open,
  onClose,
  id,
  kind,
  title,
  shared,
  activeAPI,
  robotAlwaysActive,
  userId,
  hideOptimization = false, // 👈 NUEVO: por defecto no oculta nada
}) {
  const { t } = useTranslation(["errors", "robots", "strategies"]);
  const { load: loadRobot } = useRobot();
  const { load: loadStrategy } = useStrategy();
  const history = useHistory();
  const dispatch = useDispatch();

  // si no hay menú abierto, no pintamos nada
  if (!anchor || !open) return null;

  const handleMenuClose = () => {
    onClose?.();
  };

  // ====== Navegar con ROBOT cargado (MISMA lógica que TournamentRow) ======
  const handleLoadRobot =
    (sharedId, loadDestination = "/robotvalidation") =>
    async () => {
      try {
        const { data: sharedRobot } = await customAxios.get(
          `${apiBase}sharedRobots/${sharedId}`
        );
        const robotId = sharedRobot?.robotId;
        if (!robotId) throw new Error("RobotId not found");
        await loadRobot(robotId, true);
        history.push(loadDestination);
      } catch (error) {
        console.error(error);
        dispatch(
          show({
            type: "error",
            msg: t("errors:loadRobot") || "Error loading robot",
          })
        );
      } finally {
        handleMenuClose();
      }
    };

  // ====== Navegar con ESTRATEGIA cargada (MISMA lógica que TournamentRow) ======
  const handleLoadStrategy =
    (strategyId, loadDestination = "/builder") =>
    async () => {
      try {
        await loadStrategy("S", Number(strategyId));
        history.push(loadDestination);
      } catch (error) {
        dispatch(
          show({
            type: "error",
            msg: t("errors:loadStrategy") || "Error loading strategy",
          })
        );
      } finally {
        handleMenuClose();
      }
    };

  const handleErrorRobot = (msg) => {
    dispatch(
      show({
        type: "error",
        msg: msg || t("errors:loadRobot") || "Error con el robot",
      })
    );
    handleMenuClose();
  };

  const handleErrorStrategy = (msg) => {
    dispatch(
      show({
        type: "error",
        msg: msg || t("errors:loadStrategy") || "Error con la estrategia",
      })
    );
    handleMenuClose();
  };

  const currentUserId = String(userId ?? localStorage.getItem("user_id") ?? "");

  const normalizedActiveAPI =
    typeof activeAPI === "string" && activeAPI ? activeAPI : "N";
  const normalizedShared =
    typeof shared === "boolean" || typeof shared === "number" ? shared : 1;
  const normalizedRobotAlwaysActive = Boolean(robotAlwaysActive);

  // ====== Render según tipo ======
  if (kind === "robot") {
    return (
      <RobotsPopUpMenu
        id={Number(id)}
        title={title || ""}
        anchor={anchor}
        handleClose={handleMenuClose}
        handleLoad={handleLoadRobot}
        handleErrorRobot={handleErrorRobot}
        shared={normalizedShared}
        activeAPI={normalizedActiveAPI}
        robotAlwaysActive={normalizedRobotAlwaysActive}
        handleDuplicate={() => {}}
        handleDelete={() => {}}
        t={t}
        open={open}
        userId={currentUserId}
        showConnector={false} // sin switch en Dashboard
        hideOptimization={hideOptimization} // 👈 SE PASA ABAJO
      />
    );
  }

  // Estrategias → se renderiza el PopUp con botones Constructor y Validator
  return (
    <StrategiesPopUpMenu
      id={Number(id)}
      title={title || ""}
      anchor={anchor}
      handleClose={handleMenuClose}
      handleLoad={handleLoadStrategy}
      handleErrorStrategy={handleErrorStrategy}
      shared={Boolean(normalizedShared)}
      t={t}
      open={open}
      userId={currentUserId}
      showConnector={false} // sin switch aquí también
      hideOptimization={hideOptimization} // 👈 SE PASA ABAJO
    />
  );
}

DashboardTopMenu.propTypes = {
  anchor: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  kind: PropTypes.oneOf(["robot", "strategy"]),
  title: PropTypes.string,
  shared: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  activeAPI: PropTypes.string,
  robotAlwaysActive: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideOptimization: PropTypes.bool, // 👈 añadimos al tipo
};

export default DashboardTopMenu;
